import { createElement, type ReactNode } from 'react';

const ALLOWED = new Set(['p', 'strong', 'em', 'ul', 'ol', 'li', 'br']);
const DROPPED = new Set(['script', 'style', 'iframe', 'object', 'template', 'noscript']);

/** The actions (from 0) a reply has put inside its text as buttons. */
export function inlineActions(html: string): Set<number> {
  return new Set(Array.from(html.matchAll(/<button data-action="(\d+)">/g), (m) => Number(m[1]) - 1));
}

interface Props {
  html: string;
  /** Carries out action `index` (from 0). In-text buttons work only for indexes `canInline` accepts. */
  onAction?: (index: number) => void;
  canInline?: (index: number) => boolean;
}

/**
 * The assistant's reply (9 October 2026). The server sends simple HTML (paragraphs, bold, lists, and buttons inside
 * the text for the pages it names); this rebuilds it as React elements from an allow-list, so nothing in a reply is
 * ever inserted as raw HTML. A reply kept from before (plain text or Markdown) shows as written.
 */
export function ReplyHtml({ html, onAction, canInline }: Props) {
  if (!/<(p|strong|em|ul|ol|li|br|button)\b/i.test(html)) return <div className="rr-assistant__plain">{html}</div>;
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');

  function build(node: Node, key: string): ReactNode {
    if (node.nodeType === Node.TEXT_NODE) return node.textContent;
    if (node.nodeType !== Node.ELEMENT_NODE) return null;
    const el = node as Element;
    const tag = el.tagName.toLowerCase();
    if (DROPPED.has(tag)) return null;
    const children = Array.from(node.childNodes).map((c, i) => build(c, `${key}.${i}`));
    if (tag === 'br') return createElement('br', { key });
    if (tag === 'button') {
      const index = Number(el.getAttribute('data-action')) - 1;
      // Only a button for one of this reply's own page actions does anything; anything else is its words.
      return onAction && Number.isInteger(index) && index >= 0 && (canInline?.(index) ?? false)
        ? <button key={key} type="button" className="rr-assistant__inline" data-testid="assistant-inline" onClick={() => onAction(index)}>{children}</button>
        : createElement('span', { key }, ...children);
    }
    // Anything else is unwrapped to its text: no attributes, links or styles ever reach the page.
    return ALLOWED.has(tag) ? createElement(tag, { key }, ...children) : createElement('span', { key }, ...children);
  }

  return <div className="rr-assistant__rich">{Array.from(doc.body.childNodes).map((n, i) => build(n, String(i)))}</div>;
}
